import { Component } from '@theme/component';
import { cartIsBusy } from '@theme/cart-sync';

const storageKey = 'delulu-shipping-destination';
let destination;
try { destination = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { /* Storage is optional. */ }
let rateQueue = Promise.resolve();
const pending = new Map();

async function requestRates(address, signature) {
  const key = JSON.stringify([address, signature]);
  if (pending.has(key)) return pending.get(key);
  // Shopify prepares rates per cart. Serialize differing destinations, and share
  // the same calculation between the drawer and full-cart estimator.
  const task = rateQueue.catch(() => {}).then(async () => {
    const params = new URLSearchParams();
    for (const [field, value] of Object.entries(address)) params.set(`shipping_address[${field}]`, value);
    const root = `${Theme.routes.cart_url}/`;
    const options = { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(20000) };
    const prepare = await fetch(`${root}prepare_shipping_rates.json?${params}`, { ...options, method: 'POST' });
    if (!prepare.ok) throw new Error('Address or shipping service unavailable');
    for (let attempt = 0; attempt < 15; attempt++) {
      const response = await fetch(`${root}async_shipping_rates.json?${params}`, options);
      if (!response.ok) throw new Error('Shipping unavailable');
      const data = await response.json();
      if (data?.shipping_rates) return data.shipping_rates;
      await new Promise(resolve => setTimeout(resolve, 700));
    }
    throw new Error('Shipping request timed out');
  });
  pending.set(key, task);
  rateQueue = task;
  task.finally(() => pending.delete(key)).catch(() => {});
  return task;
}

class ShippingEstimator extends Component {
  #version = 0;
  #rates = [];
  #strings;
  // This theme's retained header markup is hydrated without a live-component
  // update callback. Its server render removes this client-only readiness mark.
  #markupObserver = new MutationObserver(() => {
    if (!this.dataset.estimatorReady) this.updatedCallback();
  });

  connectedCallback() {
    super.connectedCallback();
    this.#strings = JSON.parse(this.refs.strings.textContent);
    this.#restoreDestination();
    this.dataset.estimatorReady = 'true';
    this.#markupObserver.observe(this, { attributes: true, attributeFilter: ['data-estimator-ready'] });
    document.addEventListener('delulu:cart-state', this.#cartState);
    if (destination) void this.estimate();
  }

  #restoreDestination() {
    this.refs.country.value = destination?.country || this.dataset.defaultCountry;
    if (!this.refs.country.value) this.refs.country.selectedIndex = 0;
    this.changeCountry();
    if (destination?.province) this.refs.province.value = destination.province;
    if (destination?.zip) this.refs.zip.value = destination.zip;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreDestination();
    this.dataset.estimatorReady = 'true';
    if (destination && !cartIsBusy()) void this.estimate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#version++;
    this.#markupObserver.disconnect();
    document.removeEventListener('delulu:cart-state', this.#cartState);
  }

  #cartState = event => {
    if (event.detail.busy || event.detail.uncertain) {
      this.clearEstimate();
      return;
    }
    // Cart section morphs reset the form's DOM values. Restore from the saved
    // destination only after every cart section has finished updating.
    super.updatedCallback();
    this.#restoreDestination();
    if (destination) void this.estimate();
  };

  changeCountry() {
    const provinces = JSON.parse(this.refs.country.selectedOptions[0]?.dataset.provinces || '[]');
    this.refs.province.replaceChildren(...provinces.map(([value, label]) => new Option(label, value)));
    this.refs.provinceLabel.hidden = provinces.length === 0;
    this.clearEstimate();
  }

  clearEstimate() {
    this.#version++;
    this.#rates = [];
    this.refs.results.hidden = true;
    this.refs.status.textContent = this.#strings.initial;
    this.refs.submit.disabled = false;
    this.removeAttribute('aria-busy');
  }

  async estimate(event) {
    event?.preventDefault();
    if (cartIsBusy() || !this.refs.zip.value.trim()) return;
    const version = ++this.#version;
    const address = { country: this.refs.country.value, province: this.refs.province.value, zip: this.refs.zip.value.trim() };
    destination = address;
    try { sessionStorage.setItem(storageKey, JSON.stringify(address)); } catch { /* Optional persistence. */ }
    this.refs.results.hidden = true;
    this.refs.submit.disabled = true;
    this.refs.status.textContent = this.#strings.loading;
    this.setAttribute('aria-busy', 'true');
    try {
      const rates = await requestRates(address, this.dataset.cartSignature);
      if (!this.isConnected || version !== this.#version || cartIsBusy()) return;
      this.#rates = rates.filter(rate => Number.isFinite(Number(rate.price)) && Number(rate.price) >= 0);
      if (!this.#rates.length) { this.refs.status.textContent = this.#strings.unavailable; return; }
      this.refs.rate.replaceChildren(...this.#rates.map((rate, index) => new Option(`${rate.presentment_name || rate.name} — ${this.#money(this.#ratePrice(rate), rate.currency)}`, String(index))));
      this.refs.status.textContent = `${address.country} · ${address.province ? address.province + ' · ' : ''}${address.zip}`;
      this.refs.results.hidden = false;
      this.selectRate();
    } catch (error) {
      if (this.isConnected && version === this.#version) this.refs.status.textContent = this.#strings.error;
    } finally {
      if (this.isConnected && version === this.#version) { this.refs.submit.disabled = false; this.removeAttribute('aria-busy'); }
    }
  }

  #money(cents, currency = this.dataset.currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || this.dataset.currency }).format(cents / 100);
  }

  #ratePrice(rate) {
    const cents = Math.round(Number(rate.price) * 100);
    const threshold = Number(this.dataset.freeThreshold);
    const limit = Number(this.dataset.freeRateLimit);
    // Mirror the verified FREESHIP100 eligibility. Prices remain estimates;
    // Shopify makes the final eligibility decision with the complete address.
    const eligible = this.refs.country.value === 'United States' && this.dataset.currency === 'USD'
      && (!rate.currency || rate.currency === 'USD') && threshold > 0
      && Number(this.dataset.subtotal) >= threshold && limit > 0 && cents <= limit;
    return eligible ? 0 : cents;
  }

  selectRate() {
    const rate = this.#rates[Number(this.refs.rate.value)];
    if (!rate) return;
    const price = this.#ratePrice(rate);
    this.refs.price.textContent = price === 0 ? this.#strings.free : this.#money(price, rate.currency);
    this.refs.total.textContent = rate.currency && rate.currency !== this.dataset.currency ? this.#strings.checkout_total : this.#money(Number(this.dataset.subtotal) + price);
    const us = this.refs.country.value === 'United States';
    this.refs.timing.textContent = `${this.#strings.processing} ${us ? this.#strings.us_transit : this.#strings.international_transit}`;
  }
}
if (!customElements.get('delulu-shipping-estimator')) customElements.define('delulu-shipping-estimator', ShippingEstimator);
